import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';

import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import {
  CcdlCurrencyService,
  CcdlDateFormatService,
  CcdlSpinnerService,
  SpinnerOptions,
  SpinnerType,
} from '@cpm-pr1618001/ng-design-lib';

import {
  AppConfig,
  AppInitializerEffects,
  AppInitializerFacade,
  appInitializerFeature,
  CpmGlobalErrorHandler,
  DashboardLabelsEffects,
  dashboardLabelsFeature,
  HttpInterceptorsFn,
  HttpRequestStatus,
  XsrfInterceptorFn,
} from '@cpm-pr1618003/ng-initializer-lib';

import { NG_LOCATION_TOKEN_PROVIDER } from '@cpm-pr1618002/ng-utility-lib';

import { provideEffects } from '@ngrx/effects';
import {
  catchError,
  filter,
  finalize,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { DashboardEffects } from './dashboard/store/effects/dashboard.effects';
import { dashboardFeature } from './dashboard/store/feature/dashboard.feature';
import { InitEffects } from './store/effects/init.effects';
import { InitFacade } from './store/facade/init.facade';
import { initFeature } from './store/feature/init.feature';
import { UserManagementEffects } from './store/user-management/effects/user-management.effects';
import { userManagementFeature } from './store/user-management/feature/user-management.feature';

export const initializeApp = (): any => {
  const facade = inject(AppInitializerFacade);
  const initFacade = inject(InitFacade);
  const ccdlSpinnerService = inject(CcdlSpinnerService);
  const ccdlCurrencyService = inject(CcdlCurrencyService);
  const ccdlDateFormatService = inject(CcdlDateFormatService);
  const spinnerOptions: SpinnerOptions = {
    type: SpinnerType.DUAL_RING,
    spinnerText: 'Application initializing...',
  };
  // Open before dispatching the first HTTP request so a fast response cannot race the spinner.
  ccdlSpinnerService.open(spinnerOptions);

  // Initializing parameters to start app:
  const appConfig: AppConfig = {
    excludeUrls: [
      '/auth/app-initializer',
      '/auth/refresh-token',
      '/auth/silent-refresh-token',
    ],
    bypassExcludeUrl: false,
    baseUrl: environment.apiServerHost,
    redirectUrl: environment.cuiAppUrl,
    appInitRequest: {
      appCode: '134007',
      appName: 'finance-management-ui',
    },
    shouldHandle401Errors: true,
  };
  facade.setAppConfig(appConfig);
  facade.loadRefreshToken();

  const appPromise$: Promise<boolean> = firstValueFrom(
    facade.getRefreshTokenData$.pipe(
      filter(
        (refreshToken) =>
          refreshToken?.refreshTokenRequestStatus ===
            HttpRequestStatus.Success ||
          refreshToken?.refreshTokenRequestStatus === HttpRequestStatus.Failed,
      ),
      take(1),
      switchMap((refreshToken) => {
        if (refreshToken.refreshTokenExpired) {
          window.location.assign(environment.cuiAppUrl);
          return of(false);
        }

        if (
          refreshToken.refreshTokenRequestStatus !==
            HttpRequestStatus.Success ||
          !refreshToken.userId ||
          !refreshToken.isRefreshTokenValid
        ) {
          return of(true);
        }

        facade.updateAppInitRequest({
          appCode: '134007',
          appName: 'finance-management-ui',
          userId: refreshToken.userId,
          countryCodeIso2: refreshToken.countryCode,
        });
        facade.loadAppInitializer();

        return facade.appResponse$.pipe(
          filter(
            (appResponse) =>
              appResponse?.appInitializerRequestStatus ===
                HttpRequestStatus.Success ||
              appResponse?.appInitializerRequestStatus ===
                HttpRequestStatus.Failed,
          ),
          take(1),
          switchMap((appResponse) => {
            if (
              appResponse.appInitializerRequestStatus !==
                HttpRequestStatus.Success ||
              !appResponse.isAppValid
            ) {
              return of(true);
            }

            initFacade.getUser(refreshToken.userId);
            return initFacade.isUserInfoLoaded$.pipe(
              filter(Boolean),
              take(1),
              switchMap(() =>
                initFacade.accountInfo$.pipe(
                  filter(Boolean),
                  take(1),
                  map((accountInfo) => {
                    ccdlCurrencyService.initialize(
                      accountInfo.currency,
                      accountInfo.countryCode,
                    );
                    ccdlDateFormatService.initialize(accountInfo.countryCode);
                    return true;
                  }),
                ),
              ),
            );
          }),
        );
      }),
      catchError(() => of(false)),
      finalize(() => ccdlSpinnerService.close()),
    ),
  );
  return () => appPromise$;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation(), withViewTransitions()),
    provideHttpClient(
      withInterceptors([XsrfInterceptorFn, HttpInterceptorsFn]),
      // withInterceptorsFromDi()
    ),
    provideStore(),
    provideState(appInitializerFeature),
    provideState(dashboardLabelsFeature),
    provideState(dashboardFeature),
    provideState(initFeature),
    provideState(userManagementFeature),
    provideEffects([
      AppInitializerEffects,
      DashboardLabelsEffects,
      DashboardEffects,
      InitEffects,
      UserManagementEffects,
    ]),
    environment.showDevTools
      ? provideStoreDevtools({ maxAge: 25, logOnly: true })
      : [],
    provideAppInitializer(initializeApp),
    NG_LOCATION_TOKEN_PROVIDER,
    {
      provide: ErrorHandler,
      useClass: CpmGlobalErrorHandler,
    },
  ],
};
